<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import Icon from '@ui/components/icon/Icon.vue'
import { BadgeDisplayType, BadgeVariant } from '@ui/styles'

withDefaults(
  defineProps<{
    type?: BadgeDisplayType
    variant?: BadgeVariant
    label?: string
    icon?: IconName
  }>(),
  {
    type: BadgeDisplayType.Dot,
    variant: BadgeVariant.Accent,
    label: '',
  },
)
</script>

<template lang="pug">
.badge.br-9999(:class="[type, variant]")
  template(v-if="type === BadgeDisplayType.Text")
    span.label(v-if="label") {{ $t(label) }}
    Icon.icon.flex-shrink-0(v-if="icon" :name="icon")
</template>

<style scoped>
.badge {
  /* ── Sizes ──────────────────────────────────────────────── */
  &.dot-small {
    --uno: s-6;
  }

  &.dot {
    --uno: s-8;
  }

  &.text {
    display: inline-flex;
    align-items: center;
    height: 14px;
    min-width: 14px;
    padding: 0 4px;
    gap: 2px;

    .label {
      --uno: typ-12-18-500 white-space-nowrap;
    }

    .icon {
      width: 12px;
      height: 12px;
    }
  }

  /* ── Dot colors ─────────────────────────────────────────── */
  &.dot,
  &.dot-small {
    &.accent {
      background: var(--cue-content-accent-default);
    }
    &.primary {
      background: var(--cue-content-primary-default);
    }
    &.info {
      background: var(--cue-content-info-default);
    }
    &.success {
      background: var(--cue-content-success-default);
    }
    &.error {
      background: var(--cue-content-error-default);
    }
    &.neutral {
      background: var(--cue-content-neutral-tertiary);
    }
    &.static-white {
      background: var(--cue-static-neutral-main);
    }
    &.static-dark {
      background: var(--cue-static-neutral-inverse);
    }
  }

  /* ── Text badge colors ──────────────────────────────────── */
  &.text {
    &.accent {
      background: var(--cue-bg-accent-default);
      color: var(--cue-bg-accent-on-default);
    }
    &.primary {
      background: var(--cue-bg-primary-default);
      color: var(--cue-bg-primary-on-default);
    }
    &.info {
      background: var(--cue-bg-info-default);
      color: var(--cue-bg-info-on-default);
    }
    &.success {
      background: var(--cue-bg-success-default);
      color: var(--cue-bg-success-on-default);
    }
    &.error {
      background: var(--cue-bg-error-default);
      color: var(--cue-bg-error-on-default);
    }
    &.neutral {
      background: var(--cue-bg-neutral-tertiary);
      color: var(--cue-content-neutral-primary);
    }
    &.static-white {
      background: var(--cue-static-neutral-main);
      color: var(--cue-static-neutral-inverse);
    }
    &.static-dark {
      background: var(--cue-static-neutral-inverse);
      color: var(--cue-static-neutral-main);
    }
  }
}
</style>
